/**
 * ChatSessionSidebar
 *
 * Collapsible sidebar within the chatbot panel listing all sessions
 * ordered by most recently updated. Search filter, date grouping,
 * and inline ⋮ menu with Rename and Delete.
 *
 * Requirements: 5.2, 5.3, 5.5, 5.6, 5.7, 5.8
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { MessageSquare, MoreVertical, Pencil, Trash2, Search, Star, X } from 'lucide-react';

/** Group sessions by date category */
function groupByDate(sessions) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  const weekAgo = new Date(today); weekAgo.setDate(today.getDate() - 7);

  const groups = { Today: [], Yesterday: [], 'Last 7 days': [], Older: [] };

  for (const s of sessions) {
    const d = new Date(s.updated_at || s.updatedAt || s.created_at || s.createdAt);
    if (d >= today) groups.Today.push(s);
    else if (d >= yesterday) groups.Yesterday.push(s);
    else if (d >= weekAgo) groups['Last 7 days'].push(s);
    else groups.Older.push(s);
  }

  return Object.entries(groups).filter(([, list]) => list.length > 0);
}

/**
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Array} props.sessions
 * @param {string|null} props.activeSessionId
 * @param {Function} props.onSelectSession
 * @param {Function} props.onRenameSession
 * @param {Function} props.onDeleteSession
 * @param {Array} [props.bookmarks] - bookmarked messages across sessions
 * @param {Function} [props.onUnbookmark] - remove a bookmark
 * @param {Function} [props.onGoToBookmark] - navigate to a bookmarked message's session
 */
export function ChatSessionSidebar({
  isOpen,
  sessions = [],
  activeSessionId,
  onSelectSession,
  onRenameSession,
  onDeleteSession,
  bookmarks = [],
  onUnbookmark,
  onGoToBookmark,
}) {
  const [dropdownId, setDropdownId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('sessions'); // 'sessions' | 'bookmarks'
  const inputRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!dropdownId) return;
    const handler = () => setDropdownId(null);
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  }, [dropdownId]);

  useEffect(() => {
    if (editingId && inputRef.current) inputRef.current.focus();
  }, [editingId]);

  // Filter sessions by search query
  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return sessions;
    const q = searchQuery.toLowerCase();
    return sessions.filter((s) =>
      (s.title || 'New Chat').toLowerCase().includes(q)
    );
  }, [sessions, searchQuery]);

  // Group filtered sessions by date
  const grouped = useMemo(() => groupByDate(filtered), [filtered]);

  if (!isOpen) return null;

  const startRename = (session) => {
    setEditingId(session.session_id || session.sessionId);
    setEditTitle(session.title || '');
    setDropdownId(null);
  };

  const commitRename = () => {
    if (editingId && editTitle.trim()) {
      onRenameSession?.(editingId, editTitle.trim());
    }
    setEditingId(null);
    setEditTitle('');
  };

  const handleDelete = (sessionId) => {
    onDeleteSession?.(sessionId);
    setDropdownId(null);
  };

  const renderSession = (session) => {
    const id = session.session_id || session.sessionId;
    const isActive = id === activeSessionId;
    const isEditing = id === editingId;

    return (
      <div
        key={id}
        className={`
          group/session relative px-3 py-1.5 cursor-pointer flex items-center gap-2 text-xs
          transition-colors
          ${isActive ? 'bg-white/5 text-gray-200' : 'hover:bg-white/5 text-gray-400'}
        `}
      >
        <div
          className="flex items-center gap-2 flex-1 min-w-0"
          onClick={() => !isEditing && onSelectSession?.(id)}
        >
          <MessageSquare className="w-3 h-3 shrink-0" />
          {isEditing ? (
            <input
              ref={inputRef}
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRename();
                if (e.key === 'Escape') { setEditingId(null); setEditTitle(''); }
              }}
              className="flex-1 bg-transparent border-b border-gray-500 outline-none text-xs text-gray-200"
            />
          ) : (
            <span className="truncate">{session.title || 'New Chat'}</span>
          )}
        </div>

        {!isEditing && (
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setDropdownId(dropdownId === id ? null : id); }}
              className="p-0.5 rounded opacity-0 group-hover/session:opacity-100 hover:bg-white/10 text-gray-500 hover:text-gray-300 transition-all"
            >
              <MoreVertical className="w-3 h-3" />
            </button>

            {dropdownId === id && (
              <div
                className="absolute right-0 top-full mt-1 w-28 bg-[#1e2028] border border-gray-700/50 rounded-lg shadow-lg py-1 z-[9999]"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => startRename(session)}
                  className="w-full text-left px-3 py-1.5 text-label text-gray-400 hover:bg-white/5 flex items-center gap-2"
                >
                  <Pencil className="w-2.5 h-2.5" /> Rename
                </button>
                <button
                  onClick={() => handleDelete(id)}
                  className="w-full text-left px-3 py-1.5 text-label text-red-400 hover:bg-white/5 flex items-center gap-2"
                >
                  <Trash2 className="w-2.5 h-2.5" /> Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-52 shrink-0 border-r border-gray-700/40 bg-[#1a1c22] overflow-y-auto flex flex-col">
      {/* Tab toggle: Sessions / Bookmarks */}
      <div className="flex border-b border-gray-700/30 shrink-0">
        <button
          onClick={() => setActiveTab('sessions')}
          className={`flex-1 py-1.5 text-label font-medium uppercase tracking-wider text-center transition-colors ${
            activeTab === 'sessions' ? 'text-gray-300 border-b border-blue-400' : 'text-gray-600 hover:text-gray-400'
          }`}
        >
          Sessions
        </button>
        <button
          onClick={() => { setActiveTab('bookmarks'); setSearchQuery(''); }}
          className={`flex-1 py-1.5 text-label font-medium uppercase tracking-wider text-center transition-colors flex items-center justify-center gap-1 ${
            activeTab === 'bookmarks' ? 'text-gray-300 border-b border-yellow-400' : 'text-gray-600 hover:text-gray-400'
          }`}
        >
          <Star className="w-2.5 h-2.5" /> Saved
          {bookmarks.length > 0 && (
            <span className="text-label bg-yellow-500/20 text-yellow-400 px-1 rounded">{bookmarks.length}</span>
          )}
        </button>
      </div>

      {activeTab === 'sessions' && (
        <>
          {/* Search input */}
          <div className="px-2 pt-2 pb-1">
            <div className="flex items-center gap-1.5 px-2 py-1 bg-white/[0.03] border border-gray-700/30 rounded-md">
              <Search className="w-3 h-3 text-gray-500 shrink-0" />
              <input
                ref={searchRef}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search sessions..."
                className="flex-1 bg-transparent outline-none text-label text-gray-300 placeholder:text-gray-600"
              />
            </div>
          </div>

          {/* Session list with date groups */}
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-label text-gray-600 text-center">
                {searchQuery ? 'No matching sessions' : 'No sessions yet'}
              </div>
            )}

            {grouped.map(([label, groupSessions]) => (
              <div key={label}>
                <div className="px-3 pt-2 pb-1 text-label font-medium text-gray-600 uppercase tracking-wider">
                  {label}
                </div>
                {groupSessions.map(renderSession)}
              </div>
            ))}
          </div>
        </>
      )}

      {activeTab === 'bookmarks' && (
        <div className="flex-1 overflow-y-auto">
          {bookmarks.length === 0 && (
            <div className="px-3 py-6 text-label text-gray-600 text-center">
              <Star className="w-5 h-5 mx-auto mb-2 opacity-30" />
              No saved messages yet
            </div>
          )}

          {bookmarks.map((bm) => (
            <div
              key={bm.message_id}
              className="group/bm px-3 py-2 border-b border-gray-700/20 hover:bg-white/[0.03] cursor-pointer transition-colors"
              onClick={() => onGoToBookmark?.(bm.session_id)}
            >
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-label text-gray-500 truncate">{bm.session_title}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); onUnbookmark?.(bm.message_id); }}
                  className="p-0.5 rounded opacity-0 group-hover/bm:opacity-100 hover:bg-white/10 text-gray-500 hover:text-yellow-400 transition-all"
                  title="Remove bookmark"
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </div>
              <div className="text-label text-gray-400 line-clamp-3 leading-relaxed">
                {bm.content?.slice(0, 200)}{bm.content?.length > 200 ? '...' : ''}
              </div>
              {bm.bookmarked_at && (
                <div className="text-label text-gray-600 mt-1">
                  {new Date(bm.bookmarked_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ChatSessionSidebar;
